/**
 * Retry Utilities
 * 通用重试机制工具函数
 */

export interface RetryOptions {
  /** 最大重试次数 */
  maxRetries?: number
  /** 基础延迟时间（毫秒） */
  baseDelay?: number
  /** 最大延迟时间（毫秒） */
  maxDelay?: number
  /** 超时时间（毫秒） */
  timeout?: number
  /** 重试回调 */
  onRetry?: (attempt: number, error: Error) => void
  /** 是否应该重试的判断函数 */
  shouldRetry?: (error: Error) => boolean
}

const DEFAULT_OPTIONS: Required<Omit<RetryOptions, 'onRetry' | 'shouldRetry'>> = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 5000,
  timeout: 15000,
}

/**
 * 延迟函数
 */
export const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms))

/**
 * 计算指数退避延迟时间
 */
export const getBackoffDelay = (attempt: number, baseDelay: number, maxDelay: number): number => {
  return Math.min(baseDelay * Math.pow(2, attempt), maxDelay)
}

/**
 * 带超时的 Promise 包装
 */
export function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  errorMessage = 'Operation timed out'
): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout>

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(errorMessage)), ms)
  })

  return Promise.race([promise, timeoutPromise]).finally(() => {
    clearTimeout(timeoutId)
  })
}

/**
 * 带重试的异步函数执行器
 *
 * @example
 * ```ts
 * const result = await withRetry(
 *   () => fetchData(),
 *   {
 *     maxRetries: 3,
 *     timeout: 10000,
 *     onRetry: (attempt, error) => console.log(`Retry ${attempt}: ${error.message}`)
 *   }
 * )
 * ```
 */
export async function withRetry<T>(fn: () => Promise<T>, options: RetryOptions = {}): Promise<T> {
  const {
    maxRetries = DEFAULT_OPTIONS.maxRetries,
    baseDelay = DEFAULT_OPTIONS.baseDelay,
    maxDelay = DEFAULT_OPTIONS.maxDelay,
    timeout = DEFAULT_OPTIONS.timeout,
    onRetry,
    shouldRetry = () => true,
  } = options

  let lastError: Error = new Error('Unknown error')

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // 带超时执行
      const result = await withTimeout(fn(), timeout, 'Operation timed out')
      return result
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error))

      // 检查是否应该重试
      if (attempt >= maxRetries || !shouldRetry(lastError)) {
        throw lastError
      }

      // 回调
      onRetry?.(attempt + 1, lastError)

      // 指数退避延迟
      const retryDelay = getBackoffDelay(attempt, baseDelay, maxDelay)
      await delay(retryDelay)
    }
  }

  throw lastError
}

/**
 * 创建可重试的函数
 * 返回一个包装后的函数和手动重试方法
 *
 * @example
 * ```ts
 * const { execute, retry, reset } = createRetryable(
 *   () => initSDK(),
 *   { maxRetries: 3 }
 * )
 *
 * // 首次执行
 * await execute()
 *
 * // 手动重试
 * await retry()
 * ```
 */
export function createRetryable<T>(fn: () => Promise<T>, options: RetryOptions = {}) {
  let currentAttempt = 0
  let isExecuting = false
  let lastResult: T | null = null
  let lastError: Error | null = null

  const execute = async (): Promise<T> => {
    if (isExecuting) {
      throw new Error('Already executing')
    }

    isExecuting = true
    currentAttempt = 0
    lastError = null

    try {
      lastResult = await withRetry(fn, {
        ...options,
        onRetry: (attempt, error) => {
          currentAttempt = attempt
          options.onRetry?.(attempt, error)
        },
      })
      return lastResult
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error))
      throw lastError
    } finally {
      isExecuting = false
    }
  }

  const retry = async (): Promise<T> => {
    return execute()
  }

  const reset = () => {
    currentAttempt = 0
    lastResult = null
    lastError = null
  }

  const getState = () => ({
    currentAttempt,
    isExecuting,
    lastResult,
    lastError,
  })

  return {
    execute,
    retry,
    reset,
    getState,
  }
}
